import { Component, type SyntheticEvent } from "react";
import { clone } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { type PedestrianDeviceUpdate, type PedestrianDevice } from "../../api";
import { doubleBindNullableString, doubleBindString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormUpdatePedestrianDeviceProps extends FormUpdateProps<PedestrianDevice> {}

import { store } from "../../store";

@observer
export class FormUpdatePedestrianDevice extends Component<FormUpdatePedestrianDeviceProps> {
    private validationId = createUuid();

    constructor(props: FormUpdatePedestrianDeviceProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryPedestrianDevices.validation.update.updateModel(
            this.validationId,
            this.pedestrianDeviceUpdate,
        );
    }

    componentDidMount(): void {
        store.repositoryPedestrianDevices.validation.update.initializeModel(
            this.validationId,
            this.pedestrianDeviceUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryPedestrianDevices.discardMutableCopy(
            FormUpdatePedestrianDevice.name,
            this.props.id,
        );
    }

    @computed private get pedestrianDevice(): PedestrianDevice | undefined {
        return store.repositoryPedestrianDevices.mutableCopyById(
            FormUpdatePedestrianDevice.name,
            this.props.id,
        );
    }

    @computed private get pedestrianDeviceUpdate(): PedestrianDeviceUpdate {
        const vehicle = clone(this.pedestrianDevice!);
        const update: PedestrianDeviceUpdate = vehicle;
        return update;
    }

    @action.bound
    private async updatePedestrianDevice(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const pedestrianDevice = await store.repositoryPedestrianDevices.update(
            this.props.id,
            this.pedestrianDeviceUpdate,
        );
        if (this.props.onUpdate) {
            this.props.onUpdate(pedestrianDevice);
        }
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdatePedestrianDevice.submit.text")}
                validation={store.repositoryPedestrianDevices.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updatePedestrianDevice}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formUpdatePedestrianDevice.label.label")}
                        disabled={!this.pedestrianDevice}
                        {...doubleBindNullableString(this.pedestrianDevice!, "label", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryPedestrianDevices.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formUpdatePedestrianDevice.hardwareSerialNumber.label",
                        )}
                        disabled={!this.pedestrianDevice}
                        {...doubleBindString(this.pedestrianDevice!, "hardwareSerialNumber", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryPedestrianDevices.validation.update.getFieldValidationState(
                                this.validationId,
                                "hardwareSerialNumber",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdatePedestrianDevice.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
